/**
 * messageReactionsRepository — emoji reactions on shared threads (PR-4, 2026-09-09).
 *
 * Store: `group_thread_messages/{threadId}/reactions/{messageId}` —
 * one doc per message, `byUser: { [uid]: string[] }`. A toggle is ONE
 * `setDoc(merge)` that touches only the caller's own key (arrayUnion /
 * arrayRemove), so the same write satisfies both the create and the update
 * rule and is idempotent under retries. Never a delete: the rules forbid it
 * (a re-keyed doc would orphan its history), an empty list is "no reaction".
 *
 * The listener is floored exactly like the message overlay
 * (`where('orderTs','>=',floor)` from the membership read) so a late joiner
 * never sees who reacted to a message they may not read; same fail-closed
 * flag and retry ladder as the overlay.
 */
import { arrayRemove, arrayUnion, onSnapshot, query, where } from 'firebase/firestore';
import {
  assertCurrentUid,
  collection,
  db,
  doc,
  serverTimestamp,
  setDoc,
  withBackoff,
} from './firestoreUtils';
import { resolveGroupThreadReadEnabled } from '../services/groupThreads/featureFlags';
import { getGroupThreadHistoryFloor } from '../services/groupThreads/historyFloors';
import { GROUP_LISTENER_RETRY_DELAYS_MS } from './groupThreadMessagesRepository';
import { isAllowedReactionEmoji } from '../constants/reactionEmoji';
import { parseReactionDoc, type ReactionsByMessage } from '../services/messageReactions/reactionModel';

const GROUP_STORE = 'group_thread_messages';
const REACTIONS = 'reactions';

export function reactionDocRef(threadId: string, messageId: string) {
  return doc(db, GROUP_STORE, threadId, REACTIONS, messageId);
}

export interface ToggleReactionArgs {
  threadId: string;
  messageId: string;
  /** The message's store instant (group) or the bubble's own (solo) — the rules check it. */
  orderTs: number;
  uid: string;
  emoji: string;
  /** true = add, false = remove. */
  add: boolean;
  accountId?: string | null;
}

/** Why a toggle did not land — the caller decides what to do about each. */
export type ToggleReactionRefusal = 'invalid' | 'disabled' | 'unauthenticated' | 'permission-denied' | 'error';
export type ToggleReactionResult = { ok: true } | { ok: false; reason: ToggleReactionRefusal };

const refused = (reason: ToggleReactionRefusal): ToggleReactionResult => ({ ok: false, reason });

/** True for the Firestore "rules said no" shape (code or message). */
export function isPermissionDeniedError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  if (typeof code === 'string' && /permission[-_]denied/i.test(code)) return true;
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /permission|insufficient/i.test(message);
}

/**
 * One merge write on the caller's own key. `{ok:true}` when the server took
 * it; otherwise WHY — a `permission-denied` in a solo thread usually means the
 * thread has no registry row yet (the hook registers it and retries once).
 */
export async function toggleReaction(args: ToggleReactionArgs): Promise<ToggleReactionResult> {
  const threadId = (args.threadId ?? '').trim();
  const messageId = (args.messageId ?? '').trim();
  const uid = (args.uid ?? '').trim();
  if (!threadId || !messageId || !uid) return refused('invalid');
  if (!isAllowedReactionEmoji(args.emoji)) return refused('invalid');
  if (typeof args.orderTs !== 'number' || !Number.isFinite(args.orderTs) || args.orderTs < 0) return refused('invalid');
  if (!resolveGroupThreadReadEnabled(args.accountId ? { accountId: args.accountId } : {})) return refused('disabled');
  try {
    assertCurrentUid(uid, 'toggleReaction');
  } catch {
    return refused('unauthenticated');
  }
  const ref = reactionDocRef(threadId, messageId);
  const data = {
    threadId,
    messageId,
    orderTs: Math.floor(args.orderTs),
    byUser: { [uid]: args.add ? arrayUnion(args.emoji) : arrayRemove(args.emoji) },
    updatedAt: serverTimestamp(),
  };
  try {
    await withBackoff(() => setDoc(ref, data, { merge: true }));
    return { ok: true };
  } catch (error) {
    console.warn('[messageReactionsRepository] toggle refused (non-fatal)', {
      threadId,
      messageId,
      error: error instanceof Error ? error.message : String(error),
    });
    return refused(isPermissionDeniedError(error) ? 'permission-denied' : 'error');
  }
}

/**
 * Listen to a thread's reactions above the caller's floor. Every snapshot —
 * empty included — is handed on as the full map for the thread.
 */
export function subscribeThreadReactions(
  threadId: string,
  onReactions: (reactions: ReactionsByMessage) => void,
  opts?: { accountId?: string | null },
): () => void {
  if (!resolveGroupThreadReadEnabled(opts?.accountId ? { accountId: opts.accountId } : {})) return () => {};
  const id = (threadId ?? '').trim();
  if (!id) return () => {};

  let stopped = false;
  let detachInner: (() => void) | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryIndex = 0;

  const scheduleRetry = (): void => {
    if (stopped) return;
    const delay = GROUP_LISTENER_RETRY_DELAYS_MS[Math.min(retryIndex, GROUP_LISTENER_RETRY_DELAYS_MS.length - 1)];
    retryIndex += 1;
    retryTimer = setTimeout(attach, delay);
  };

  const attach = (): void => {
    if (stopped) return;
    try {
      // The floor is read at every (re)attach — a floor resolving after the
      // first attempt is picked up by the retry, exactly like the overlay.
      const floored = query(collection(db, GROUP_STORE, id, REACTIONS), where('orderTs', '>=', getGroupThreadHistoryFloor(id)));
      const unsubscribe = onSnapshot(
        floored,
        (snap) => {
          retryIndex = 0;
          const out: ReactionsByMessage = {};
          for (const d of snap.docs) {
            const parsed = parseReactionDoc(d.id, d.data());
            if (parsed) out[parsed.messageId] = parsed;
          }
          onReactions(out);
        },
        () => {
          try { unsubscribe(); } catch { /* already detached */ }
          scheduleRetry();
        },
      );
      detachInner = unsubscribe;
    } catch {
      scheduleRetry();
    }
  };

  attach();

  return () => {
    stopped = true;
    if (retryTimer !== null) clearTimeout(retryTimer);
    try { detachInner?.(); } catch { /* already detached */ }
  };
}
